// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Misc dependencies.
import { capitalize } from '../../../utils/string';

// Model objects.
import DisplayItem from '../../../model/entity/DisplayItem/DisplayItem';

/**
 * Custom Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Items
 */
class Custom extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Dispatches custom item mounted event.
   */
  componentDidMount() {
    // Get Event service.
    const eventService = NGXD.getServiceContainer().getService('event');

    eventService.trigger('ugcwall:item:custom:mounted', {
      index: this.props.customItemIndex,
      item:  this.props.customItem,
    });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const { item, customItem } = this.props;

    // Combine standard classes with custom classes.
    const _standardClasses = customClassNamesFromModel(item, this.props.templateOpts, customItem.prominence);
    const _customClasses = customItem.classes;
    const _classes = _standardClasses.concat(_customClasses);

    // Item model.
    const displayItemModel = {
      className: _classes.join(' '),
    };

    // Use any defined custom event handlers.
    if (customItem.events) {
      // Click event.
      if (customItem.events.click) {
        displayItemModel.onClick = () => {
          try {
            customItem.events.click(customItem);
          } catch (exception) {}
        };
      }
    }

    // Custom render function.
    let _content;
    if (customItem.render) {
      _content = customItem.render(customItem, this.props.customItemIndex);
    } else {
      _content = '<div>Please implement a render() method for this custom item.</div>';
    }

    return (
      <div {...displayItemModel}>
        <div className="xItemInner">
          <div dangerouslySetInnerHTML={{ __html: _content }} />
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
Custom.propTypes = {
  item: PropTypes.instanceOf(DisplayItem),
};

/**
 * React default props.
 *
 * @type {Object}
 */
Custom.defaultProps = {
  item:         {},
  templateOpts: {},
  itemOpts:     {},
};

export default Custom;

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */

/**
 * Get list of classes dependant on template options and display item content
 *
 * @private
 *
 * @param {object} item - Object representing display item
 * @param {object} template - Object representing template options
 * @param {object} displayStrategy - Item prominence
 */
function customClassNamesFromModel(item, template, displayStrategy) {
  const classes = ['xItem', 'xCustomItem'];

  const _displayStrategy = displayStrategy ? displayStrategy : item.strategy.displayStrategy;

  classes.push('xDisplay' + capitalize(_displayStrategy || 'show'));

  if (template) {
    if (template.itemTemplate) classes.push('x' + capitalize(template.itemTemplate.toLowerCase()));
    if (template.variant) classes.push('xVariant' + capitalize(template.variant));
  }

  return classes;
}
